import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent, Grid, Button, Chip, LinearProgress, Alert } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAppSelector } from 'src/store';
import PlansAndBillingService from 'src/services/plans_and_billing';
import { PlansAndBilling } from './onboarding/v1/PlansAndBilling';
import { SubscriptionStatus, UsageData } from 'src/types/billing';

const PrivateBillingAndPlans: React.FC = () => {
  const theme = useTheme();
  const darkMode = theme.palette.mode === 'dark';
  const { user } = useAppSelector((state) => state.user);

  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [usageData, setUsageData] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch subscription status and usage data in parallel
      const [statusResponse, usageResponse] = await Promise.all([
        PlansAndBillingService.getCurrentSubscriptionStatus(),
        PlansAndBillingService.getCurrentUsage()
      ]);

      setSubscriptionStatus(statusResponse);
      setUsageData(usageResponse);
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError('Failed to load subscription information');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (state: string) => {
    switch (state) {
      case 'active':
        return 'success';
      case 'trialing':
        return 'info';
      case 'past_due':
        return 'warning';
      case 'canceled':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading subscription information...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={fetchUserData}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        background: darkMode ? theme.palette.background.default : '#fff',
        color: darkMode ? theme.palette.text.primary : '#000',
        flex: 1,
        minHeight: '100vh',
        px: { xs: 2, sm: 4, md: 6 },
        py: { xs: 4, sm: 5, md: 7 },
        transition: 'all 0.3s ease',
        width: '100%',
      }}
    >

      {/* Plans and Billing Component */}
      <Box>

        <PlansAndBilling
          usageData={usageData}
          loading={loading}
          error={error}
          onRetry={fetchUserData}
        />
      </Box>
    </Box>
  );
};

export default PrivateBillingAndPlans;
